import { Link, useParams } from "react-router-dom";
import ExcelJS from "exceljs";
import { Close, ErrorOutline } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import "./ADDBuying.css";
import { ArrowBack, CheckCircleOutline } from "@mui/icons-material";
import Attachment from "../../attachment/Attachment";
import { labelToKey } from "../../../utilities/utils";
import { boFields, currencyList } from "../../../utilities/pofields";
import {
  boSheetDownload,
  getPOBuffer,
} from "../../../utilities/excelUtilities";
import { LoadingButton } from "@mui/lab";

export default function AddBuyingOrder() {
  const params = useParams();
  const vendorCode = params.vendorCode;
  const [vendor, setVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [currency, setCurrency] = useState("INR");
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [buyingAtt, setBuyingAtt] = useState(null);
  const [submit, setSubmit] = useState(false);
  const [poAtt, setPOAtt] = useState(null);
  const [poCode, setPOCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isPOSubmitted, setPOSubmitted] = useState(false);
  const [createdBy, setCreatedBy] = useState("");
  const [poType, setPOType] = useState("Fresh");
  const [missingSKUs, setMissingSKUs] = useState([]);
  const [showMissingPopup, setShowMissingPopup] = useState(false);
  const [openSkuDialog, setOpenSkuDialog] = useState(false);


  useEffect(() => {
    fetch(`${process.env.REACT_APP_SERVER_URL}/vendor/all`)
      .then((response) => response.json())
      .then((res) => {
        setVendors(res.data.vendors);
        setLoading(false);
      })
      .catch((error) => {
        console.error("API error:", error);
        // Handle the error
      });
    if (vendorCode !== "new")
      fetch(`${process.env.REACT_APP_SERVER_URL}/vendor/${vendorCode}`)
        .then((response) => response.json())
        .then((res) => {
          setVendor(res.data.vendor);
          setLoading(false);
        })
        .catch((error) => {
          console.error("API error:", error);
        });

    const poCodeUrl = `${process.env.REACT_APP_SERVER_URL}/purchase-order/`;
    fetch(poCodeUrl)
      .then((response) => response.json())
      .then((res) => {
        setPOCode(res.data.poCode);
      });
    return () => {
      console.log("Component unmounted");
    };
  }, [vendorCode]);

  const updateVendor = async (newVendor) => {
    if (newVendor?.vendorCode)
      fetch(
        `${process.env.REACT_APP_SERVER_URL}/vendor/${newVendor.vendorCode}`
      )
        .then((response) => response.json())
        .then((res) => {
          setVendor(res.data.vendor);
        })
        .catch((error) => {
          console.error("API error:", error);
        });
    else setVendor(null);
  };

  const updateFile = async (file) => {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(file);

    const worksheet = workbook.getWorksheet(1);
    const parsedData = [];
    let labels = [];
    worksheet.eachRow((row, rowIndex) => {
      if (rowIndex === 1) {
        labels = row.values.slice(1);
      } else {
        let rowData = row.values.slice(1);
        rowData = rowData.map((data) =>
          typeof data === "object" ? data.text : data
        );
        const skuFieldsData = {};
        rowData.forEach((value, index) => {
          const key = labelToKey(labels[index], boFields);
          if (key) skuFieldsData[key] = value;
        });
        parsedData.push(skuFieldsData);
      }
    });

    setExcelData(parsedData);
    setBuyingAtt(file);
  };

  const getPOAtt = async () => {
    if (poAtt) return poAtt;

    if (!vendor) {
      throw new Error("Vendor is required to generate PO");
    }

    const poBuffer = await getPOBuffer(
      excelData,
      vendor,
      poCode,
      currency,
      paymentTerms,
      createdBy,
      estimatedDeliveryDate
    );

    setPOAtt(poBuffer);
    return poBuffer;
  };

  const submitPOForVerification = async (e) => {
    e.preventDefault();
    
      if (submitLoading) return;

      setSubmit(true);
      setSubmitLoading(true);      

    if (!buyingAtt || !vendor || !createdBy) {
        alert("Please fill all required fields");
        return;
    }

    try {
      // 1️⃣ Collect SKU codes from Excel
      const skuCodes = excelData
        .map((r) => String(r.skuCode || "").trim())
        .filter(Boolean);

      // 2️⃣ Validate SKUs from backend
      const skuCheckRes = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/purchase-order/sku/validate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ skuCodes }),
        }
      );

      if (!skuCheckRes.ok) {
      throw new Error("SKU validation failed");
    }

      const skuCheck = await skuCheckRes.json();

      // ❌ Missing SKUs found → STOP & SHOW POPUP
      if (skuCheck.missingSKUs.length > 0) {
        setMissingSKUs(skuCheck.missingSKUs);
        setShowMissingPopup(true);
        setSubmitLoading(false);
        return;
      }
      const formData = new FormData();
      formData.append("currency", currency);
      if (paymentTerms) formData.append("paymentTerms", paymentTerms);
      if (estimatedDeliveryDate)
        formData.append("estimatedDeliveryDate", estimatedDeliveryDate);

      formData.append("records", JSON.stringify(excelData));
      formData.append("vendorCode", vendor.vendorCode);
      formData.append("createdBy", createdBy);
      formData.append("poType", poType);
      formData.append("poCode", poCode);

      const poBuffer = await getPOAtt();

      const blob = new Blob([poBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const excelFile = new File([blob], "po.xlsx", { type: blob.type });
      formData.append("poAttachment", excelFile);

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/purchase-order/new`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
        }

      const newPO = await response.json();

      if (newPO.success) {
        setPOSubmitted(true);
      }
    } catch (err) {
    console.error("PO submit failed:", err);
    alert(
        "PO submission failed due to server timeout. Please try again or contact tech team."
    );
    } finally {
    setSubmitLoading(false);
    }
  };

  const previewPO = async (e) => {
    e.preventDefault();
    setSubmit(true);

    if (!buyingAtt) return;

    if (!vendor) {
      alert("Please select Vendor before previewing PO");
      return;
    }

    if (!createdBy) {
      alert("Please enter Your Email");
      return;
    }

    setPreviewLoading(true);

    try {
      const poAtt = await getPOAtt();
      const url = URL.createObjectURL(new Blob([poAtt]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "po.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading)
    return (
      <Container maxWidth="sm" style={{ marginTop: "2rem" }}>
        <Paper elevation={3} style={{ padding: "2rem", textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="h4" gutterBottom>
            Loading...
          </Typography>
        </Paper>
      </Container>
    );
  else if (isPOSubmitted)
    return (
      <Container maxWidth="sm" style={{ marginTop: "2rem" }}>
        <Paper elevation={3} style={{ padding: "2rem", textAlign: "center" }}>
          <CheckCircleOutline sx={{ fontSize: 100, color: "green" }} />
          <Typography variant="h4" gutterBottom>
            Submission Successful
          </Typography>
          <Typography variant="body1">
            Your PO has been submitted for verification.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: "1rem" }}
            onClick={() => window.location.reload()}
          >
            Go Back
          </Button>
        </Paper>
      </Container>
    );

  return (
    <div className="new-pos">
      <Link to="/admin">
        <IconButton
          edge="start"
          color="inherit"
          aria-label="back"
          style={{ color: "black" }}
        >
          <ArrowBack />
          Back
        </IconButton>
      </Link>
      <form onSubmit={submitPOForVerification}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={6}>
            <h1>PO Creation</h1>
          </Grid>
          <Grid item xs={6} style={{ margin: "auto" }}>
            <Attachment
              label="Download Sample Buying Sheet Excel"
              downloadFile={boSheetDownload}
            />
          </Grid>
          <Grid item xs={12}>
            <h2>Vendor Details</h2>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              required
              disablePortal
              id="vendor-code"
              options={vendors}
              getOptionLabel={(option) => option.vendorCode || ""}
              isOptionEqualToValue={(option, value) =>
                option.vendorCode === value?.vendorCode
              }
              value={vendor}
              onChange={(e, newValue) => updateVendor(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Vendor Code"
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              required
              disablePortal
              id="company-name"
              options={vendors}
              getOptionLabel={(option) => option.companyName || ""}
              isOptionEqualToValue={(option, value) =>
                option.vendorCode === value?.vendorCode
              }
              value={vendor}
              onChange={(e, newValue) => updateVendor(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Company Name"
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="product-category"
              label="Product Category"
              value={vendor ? vendor.productCategory : ""}
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              required
              disablePortal
              id="currency"
              options={currencyList}
              renderInput={(params) => (
                <TextField
                  required
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "new-password",
                    style: { width: "auto" },
                  }}
                  label="Currency"
                />
              )}
              value={currency}
              size="small"
              onChange={(e, newValue) => setCurrency(newValue)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="PO Type"
              value={poType}
              onChange={(e) => setPOType(e.target.value)}
              fullWidth
              size="small"
              SelectProps={{ native: true }}
            >
              <option value="Fresh">Fresh</option>
              <option value="LSL">LSL</option>
            </TextField>
          </Grid>

          {poType === "LSL" && (
            <Grid item xs={6}>
              <Typography
                variant="body2"
                style={{
                  color: "#d97706",
                  background: "#fff7ed",
                  padding: "8px 12px",
                  borderRadius: "6px",
                }}
              >
                ⚠️ LSL PO is a master PO. Multiple invoices (GRNs) can be added
                later.
              </Typography>
            </Grid>
          )}

          <Grid item xs={6}>
            <TextField
              id="po-code"
              label="PO Code"
              value={poCode}
              onChange={(e) => setPOCode(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="date"
              id="estimated-delivery-dates"
              label="Estimated Delivery Date"
              value={estimatedDeliveryDate}
              fullWidth
              size="small"
              onChange={(e) => setEstimatedDeliveryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="payment-terms"
              label="Payment Terms"
              value={paymentTerms}
              fullWidth
              size="small"
              onChange={(e) => setPaymentTerms(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              id="created-by"
              label="Your Email"
              value={createdBy}
              fullWidth
              size="small"
              onChange={(e) => setCreatedBy(e.target.value)}
              type="email"
            />
          </Grid>
          <Grid item xs={6}>
            <Attachment
              label="Upload Buying Order Sheet"
              file={buyingAtt}
              updateFile={(file) => updateFile(file)}
              required={true}
              submit={submit}
              fileType=".xlsx"
            />
          </Grid>
          {excelData.length > 0 && (
            <Grid item xs={12}>
              <TableContainer component={Paper}>
                <Table
                  style={{ tableLayout: "auto" }}
                  size="small"
                  aria-label="a dense table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell style={{width: '10px'}}><b>S.N.</b></TableCell>
                      {boFields.map((tableHead, i) => (
                        <TableCell key={i}>{tableHead.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {excelData.map((row, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell>{i + 1}</TableCell>
                        {boFields.map((boField, i) => (
                          <TableCell
                            style={{
                              whiteSpace: "nowrap",
                            }}
                            key={i}
                          >
                            {row[boField.key]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {submitLoading && (
                  <div className="table-loading-overlay">
                    <CircularProgress />
                    <p>Submitting PO...</p>
                  </div>
                )}
              </TableContainer>
            </Grid>
          )}
          <Grid item xs={6}>
            {previewLoading ? (
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                size="medium"
                fullWidth
                loading
              >
                Preview PO
              </LoadingButton>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="medium"
                fullWidth
                onClick={previewPO}
                type="button"
              >
                Preview PO
              </Button>
            )}
          </Grid>
          <Grid item xs={6}>
            <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                size="medium"
                fullWidth
                loading={submitLoading}
                loadingPosition="center"
                disabled={submitLoading}
                >
                Send PO For Verification
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
      <Dialog
        open={showMissingPopup}
        onClose={() => setShowMissingPopup(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#fee2e2",
            color: "#991b1b",
            fontWeight: 600,
          }}
        >
          ⚠️ Missing SKUs Found
          <IconButton onClick={() => setShowMissingPopup(false)}>
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            The following <b>{missingSKUs.length}</b> SKU(s) from your Excel
            file do not exist in the system.
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              p: 1,
              backgroundColor: "#fafafa",
            }}
          >
            <List dense>
              {missingSKUs.map((sku, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <ErrorOutline sx={{ color: "#dc2626" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={sku}
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Typography sx={{ mt: 2, fontSize: 13, color: "text.secondary" }}>
            Please create these SKUs first or correct the Excel sheet before submitting the PO.
          </Typography>
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={() => setShowMissingPopup(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
